// /devices/:id — 單機詳情（spec 00 §7.2 / 16 §4.3 / 21 cold-start）。
// 靜態/業務面走 REST GET /v3/devices/{id}（00 §8.4）；本頁僅顯示，
// recording 三業務欄位的寫入在裝置設定頁（ADR-046：LS app 走 ws op:set；restful PUT 供 3rd-party），不在本頁。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isPhoneCam, phoneize, PhoneCamContext } from '../../lib/phoneCam';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { DEGRADED_BANNER_TEXT, useConnectionMode } from '../../lib/connection';
import { formatBytes, formatRelativeUs, formatTimestampUs } from '../../lib/format';
import { signaling } from '../../lib/ws';
import type { SignalFrame } from '../../lib/ws';
import { isRecordSettingReport } from './settings/ids';
import type { DeviceSummary } from './deviceModel';
import { isPanelDevice, batterySummary, capabilityPendingText, recordingModeLabel, cameraClass, capabilityLabel, deviceName, deviceStateLabel, eventTypeLabel, isSimulated, subscriptionExpired, tileState } from './deviceModel';
import { DeviceSettings } from './settings/DeviceSettings';
import { PanelDetail } from './PanelDetail';
import { DEVICE_INCLUDE } from './useDeviceList';
import './devices.css';

export default function DeviceDetailPage() {
  const { t: tBase } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const deviceId = id ?? '';
  const [device, setDevice] = useState<DeviceSummary | null>(null);
  // MyCam 手機攝影機（ls-loop MW）：頁面上的「SD 卡」都換成「手機空間」；下面的設定區塊經 PhoneCamContext 也一樣
  const phone = isPhoneCam(device?.model);
  // 要 memo：t 在 load 的依賴裡，每次 render 換新 → load 換新 → effect 重抓 → setDevice → 再 render，無限打 API
  const t = useMemo(
    () => ((...a: unknown[]) => phoneize(String((tBase as unknown as (...x: unknown[]) => unknown)(...a)), phone)) as unknown as typeof tBase,
    [tBase, phone]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // W2-02：degraded/offline → 即時影像入口 disable（spec 10 §4.3）
  const liveDisabled = useConnectionMode() !== 'online';
  const alive = useRef(true);

  // background=true（事件觸發的 refresh）：失敗時保留現有畫面，不升級成整頁 error card
  //（error card 會 unmount DeviceSettings → useDeviceRoom 退訂 → 收不到後續事件，頁面無法自癒）。
  const load = useCallback(
    async (background = false) => {
      if (!deviceId) return;
      if (!background) {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await apiFetch<DeviceSummary>(`/v3/devices/${encodeURIComponent(deviceId)}`, {
          query: { include: DEVICE_INCLUDE },
        });
        if (alive.current) setDevice(res.data);
      } catch (e) {
        if (!alive.current || background) return;
        if (e instanceof ApiError && e.status === 404) setError(t('devices.deviceNotFound'));
        else if (e instanceof ApiError) setError(`${e.message}（${e.code}）`);
        else setError(e instanceof Error ? e.message : t('common.loadFailed'));
      } finally {
        if (alive.current && !background) setLoading(false);
      }
    },
    [deviceId, t]
  );

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  // 屬性變更回報（ADR-046 / common/04 §5.11）：裝置在錄影設定值有變的 op:set 成功後主動
  // 廣播 op:report（無 request_id，id = 三錄影 attr 之一）→ refetch，讓 dv-kv 的「錄影設定」
  // 列即時反映。room 訂閱由本頁的 DeviceSettings（useDeviceRoom）持有。
  useEffect(() => {
    if (!deviceId) return;
    const off = signaling.on('event', (frame: SignalFrame) => {
      if (frame.device_id !== deviceId || !isRecordSettingReport(frame)) return;
      void load(true);
    });
    return off;
  }, [deviceId, load]);

  if (loading && !device) {
    return (
      <section className="card dv-state-card" aria-busy="true">
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('devices.deviceLoading')}
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="card dv-state-card">
        <p className="error-text" style={{ margin: 0 }}>
          {error}
        </p>
        <div style={{ display: 'flex', gap: 'var(--spacing-4)' }}>
          <button className="btn-primary" onClick={() => void load()}>
            {t('common.retry')}
          </button>
          <Link className="btn-ghost" to="/devices">
            {t('devices.backToList')}
          </Link>
        </div>
      </section>
    );
  }

  if (!device) return null;
  // ls-loop 家用面板：不是攝影機，改用面板頁（狀態 + 移除，V-16）
  if (isPanelDevice(device)) return <PanelDetail device={device} onRemoved={() => navigate('/devices')} />;

  const st = tileState(device);
  const owner = device.role === 'owner';
  const expired = owner && subscriptionExpired(device);
  // 21 §2.4 cold-start：剛綁定、device 尚未首次 sign-in → capability_effective 為空
  const coldStart = !device.capability_effective || device.capability_effective.length === 0;
  // LV 入口 capability 閘（16 §3.2 過濾規則延伸至詳情頁，2026-06-12）：
  // non-camera →「即時影像」整顆不渲染（21 §0 未列 cluster 不顯示控制項 + 21 §2.4
  // 不顯示不支援殘影，同 21 §7 0x8050 gate 先例）；unknown = cold-start → disable +
  // 「等待裝置首次連線…」，不得判為非攝影機。
  const cc = cameraClass(device);
  const recMode = device.recording?.mode;
  const powerLabel = batterySummary(device);

  return (
    <PhoneCamContext.Provider value={phone}>
    <section>
      <header className="dv-detail-head">
        <div>
          <h1>
            {deviceName(device)}
            {isSimulated(device) && <span className="dv-badge dv-badge-sim" data-testid="sim-tag">{t('devices.simulated')}</span>}
          </h1>
          <div className="dv-detail-sub">
            <span className={`dv-dot dv-dot-${st}`} />
            <span className="text-secondary">
              {deviceStateLabel(device)}
              {device.online?.last_seen != null ? t('devices.lastSeenSuffix', { time: formatRelativeUs(device.online.last_seen) }) : ''}
            </span>
            <span className="dv-badge">{device.model ?? t('devices.unknownModel')}</span>
            {device.firmware_version && <span className="dv-badge">FW {device.firmware_version}</span>}
            {device.role && device.role !== 'owner' && <span className="dv-badge">{t('devices.sharedWithMe')}</span>}
          </div>
        </div>
        <Link className="btn-ghost" to="/devices">
          {t('devices.backToList')}
        </Link>
      </header>

      {expired && <div className="lv-banner">{t('devices.cloudRecordingDisabledDetail')}</div>}

      <nav className="dv-detail-nav" aria-label={t('devices.deviceFunctions')}>
        {cc === 'non-camera' ? null : cc === 'unknown' ? (
          <button className="btn-primary" disabled title={capabilityPendingText()}>
            {t('devices.liveVideo')}
          </button>
        ) : st === 'offline' ? (
          <button className="btn-primary" disabled title={t('devices.liveOfflineTitle')}>
            {t('devices.liveVideoOffline')}
          </button>
        ) : liveDisabled ? (
          <button className="btn-primary" disabled title={DEGRADED_BANNER_TEXT}>
            {t('devices.liveVideo')}
          </button>
        ) : (
          <Link className="btn-primary" to={`/devices/${encodeURIComponent(deviceId)}/live`}>
            {t('devices.liveVideo')}
          </Link>
        )}
        <Link className="btn-ghost" to={`/playback/${encodeURIComponent(deviceId)}`}>
          {t('devices.playback')}
        </Link>
        <Link className="btn-ghost" to={`/events/${encodeURIComponent(deviceId)}`}>
          {t('devices.events')}
        </Link>
        <Link className="btn-ghost" to={`/rec-timeline/${encodeURIComponent(deviceId)}`}>
          {t('devices.recTimeline')}
        </Link>
        <Link className="btn-ghost" to={`/rec-continuous/${encodeURIComponent(deviceId)}`}>
          {t('devices.recContinuous')}
        </Link>
        {/* 本地相簿(APP#42):此裝置的截圖 / 錄影 */}
        <Link className="btn-ghost" to={`/gallery/${encodeURIComponent(deviceId)}`}>
          {t('devices.gallery')}
        </Link>
        {owner && (
          <Link className="btn-ghost" to={`/sharing/${encodeURIComponent(deviceId)}`}>
            {t('devices.sharingManagement')}
          </Link>
        )}
        {owner && (
          <Link className="btn-ghost" to={`/account/subscription/${encodeURIComponent(deviceId)}`}>
            {t('devices.subscriptionPlan')}
          </Link>
        )}
      </nav>

      <div className="card dv-info-card">
        <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('devices.deviceInfo')}</h2>
        {coldStart && (
          <p className="text-secondary" style={{ marginTop: 0 }}>
            {t('devices.coldStartInfo')}
          </p>
        )}
        <dl className="dv-kv">
          <dt>{t('devices.deviceId')}</dt>
          <dd>{device.device_id}</dd>
          <dt>{t('devices.model')}</dt>
          <dd>{device.model ?? '—'}</dd>
          <dt>{t('devices.firmwareVersion')}</dt>
          <dd>
            {device.firmware_version ?? '—'}
            {device.firmware_upgrade?.needs_upgrade && (
              <span className="error-text" style={{ marginLeft: 'var(--spacing-3)' }}>
                {t('devices.upgradableTo', { version: device.firmware_upgrade.latest_version ?? t('devices.newVersion') })}
              </span>
            )}
          </dd>
          {powerLabel && (
            <>
              <dt>{t('devices.powerStatus')}</dt>
              <dd>{powerLabel}</dd>
            </>
          )}
          <dt>{t('devices.recordingSettings')}</dt>
          <dd>
            {recMode ? recordingModeLabel(recMode) : '—'}
            {device.recording?.location ? `・${device.recording.location === 'cloud' ? t('devices.locationCloud') : device.recording.location === 'sdcard' ? t('deviceSettings.recordLocationSdcard') : device.recording.location}` : ''}
            {device.recording?.quality ? `・${device.recording.quality}` : ''}
          </dd>
          {device.sd_card && (
            <>
              <dt>{t('devices.sdCard')}</dt>
              <dd>
                {device.sd_card.present
                  ? `${
                      device.sd_card.used_mb != null && device.sd_card.total_mb != null
                        ? `${formatBytes(device.sd_card.used_mb * 1048576)} / ${formatBytes(device.sd_card.total_mb * 1048576)}`
                        : t('devices.sdInserted')
                    }${device.sd_card.health ? `・${device.sd_card.health === 'ok' ? t('devices.sdHealthOk') : device.sd_card.health}` : ''}`
                  : t('devices.sdNotInserted')}
              </dd>
            </>
          )}
          {device.last_event?.event_ts != null && (
            <>
              <dt>{t('devices.latestEvent')}</dt>
              <dd>
                {t('devices.eventWithTime', { event: eventTypeLabel(device.last_event.event_type), time: formatRelativeUs(device.last_event.event_ts) })}
              </dd>
            </>
          )}
          {!coldStart && (
            <>
              <dt>{t('devices.supportedFeatures')}</dt>
              <dd>
                {/* 不裸秀 raw hex cluster:映射為使用者可讀功能名,去重,隱藏未知/系統面（design review P0） */}
                {phoneize([...new Set((device.capability_effective ?? []).map((c) => capabilityLabel(c.cluster ?? '')).filter(Boolean))].join('、'), phone) || '—'}
              </dd>
            </>
          )}
          {owner && device.subscription && (
            <>
              <dt>{t('devices.subscription')}</dt>
              <dd>
                {device.subscription.plan ?? '—'}・{device.subscription.status ?? '—'}
                {device.subscription.expired_time != null ? t('devices.expiresSuffix', { time: formatTimestampUs(device.subscription.expired_time, { second: undefined }) }) : ''}
              </dd>
            </>
          )}
          {owner && device.registered_at != null && (
            <>
              <dt>{t('devices.boundAt')}</dt>
              <dd>{formatTimestampUs(device.registered_at, { second: undefined })}</dd>
            </>
          )}
        </dl>
      </div>

      {/* 裝置設定（spec 21）：capability-driven sections + 改名 + 錄影(DEC-09) + 維運/OTA + 解除綁定 */}
      <DeviceSettings device={device} onDeviceUpdate={setDevice} onUnbound={() => navigate('/devices', { replace: true })} onFirmwareUpdated={() => void load(true)} />
    </section>
    </PhoneCamContext.Provider>
  );
}
